import {
  Step,
  Task,
  LogTimer,
  Notification,
  Trigger,
  TaskForm,
  Template,
  Snippet
} from "./entities";

export class QueryColumns {
  constructor(params, data) {
    this.data = this.make(params, data);
  }

  make(params, { requestSettings, generalSettings }) {
    const all = params.map(({ tasks, ...rest }, stepIndex) => {
      const tasks_built = tasks.data.map((tk) => {
        const map_task = new Task({
          ticket_: tk.attributes.ticket,
          created_at: tk.attributes.created_at,
          updated_at: tk.attributes.updated_at,
          is_request: tk.attributes.is_request,
          master_id: tk.attributes.master_id,
          is_form: tk.attributes.is_form,
          archivers: tk.attributes.archivers,
          started_at: tk.attributes.started_at,
          due_date: tk.attributes.closed_at,
          project_name: tk.attributes.project_name,
          request_data: tk.attributes.request_data,
          put_to_step_at: tk.attributes.put_to_step_at,
          put_to_step: tk.attributes.put_to_step,
          has_notification: tk.attributes.has_notification,
          has_client_reply: tk.attributes.has_client_reply,
          ticket_number: tk.attributes.ticket_number,
          position: tk.attributes.position,
          requestSettings: requestSettings,
          generalSettings: generalSettings,
          approval_stage: tk.attributes.approval_stage,
          cron_expression:
            tk.relationships.schedules.data[0]?.attributes.frequency,
          files_number: tk.attributes.files_number,
          comments_number: tk.attributes.comments_number,
          notes_number: tk.attributes.notes_number,

          subtasks_number: tk.attributes.subtasks_number,
          incomplete_subtasks_number: tk.attributes.incomplete_subtasks_number,
          is_completed: tk.attributes.is_completed,
          is_closed: tk.attributes.is_closed,
          task_system_id: tk.attributes.task_system_id,
          title: tk.attributes.title,
          description: tk.attributes.description,
          step_id: tk.attributes.step_id,
          owner: tk.attributes.task_user_id,
          is_follow: tk.attributes.is_follow,
          spentTime: tk.attributes.spent_time,
          task_form_data: tk.attributes.task_form_data,
          id: tk.id,
          projectId: tk.relationships.steps.data.attributes.project_id,
          assignees: tk.relationships.assignees.data.map(
            ({ attributes, id }) => {
              return {
                id,
                title: `${attributes.first_name} ${attributes.last_name}`,
                alt: `${attributes.first_name} ${attributes.last_name}`,
                subTitle: attributes.email,
                background: attributes.color,
                phone: attributes.phone,
              };
            }
          ),
          master:
            tk.relationships.master.length === undefined
              ? tk.relationships.master.data[0]
              : null,

        slaves: tk.relationships.slaves.data.map(({ attributes, id }) => {
          return {
            id,
            ...attributes,
          };
        }),

          client_user: tk.attributes.client_user
            ? {
                ...tk.attributes.client_user,
                title: `${tk.attributes.client_user.first_name} ${tk.attributes.client_user.last_name}`,
              }
            : null,
          clients: tk.relationships.clients.data.map(({ attributes, id }) => {
            return {
              id,
              userID: attributes.userID,
              locationId: attributes.locationId,
              title: `${attributes.firstName} ${attributes.lastName}`,
              alt: `${attributes.firstName} ${attributes.lastName}`,
              subTitle: attributes.email,
              phone: attributes.phone,
              background: attributes.color,
              account: attributes.account,
              sub_account_name: attributes.sub_account_name,
            };
          }),
          tags: tk.relationships.tags.data.map((p) => ({
            id: p.id,
            label: p.attributes.label,
            variant: p.attributes.variant,
          })),
          priority: tk.attributes.priority,
          estimated_time: tk.attributes.estimated_time,
          latest_feedback: tk.attributes.latest_feedback,
          column: {
            ...rest,
            task_count: tasks.data.length,
            index: stepIndex,
          },
          added: tk.attributes.added,
        });

        map_task.resolveAll();
        return map_task;
      });

      const step_built = new Step({
        id: rest.id,
        ids: rest.ids,
        index: stepIndex,
        created_at: rest.created_at,
        updated_at: rest.updated_at,
        name: rest.name,
        position: rest.position,
        project_id: rest.project_id,
        total: rest.total_tasks,
        color: rest.color,
        encryptId: rest.encryptId || null,
        tasks: tasks_built,
        page: 1,
        canLoad: true,
      });

      return step_built;
    });

    return all;
  }

  run() {
    return this.data;
  }
}

export class QueryLogTimer {
  constructor(params, project) {
    this.data = this.make(params, project);
  }

  make(timers, project) {
    // Defensive mapping: a single malformed row (soft-deleted task, removed
    // task_user, missing relationship) used to throw here, and the caller's
    // catch turned the whole list into [] — the panel then showed
    // "No time logs yet" while the card kept the real total. Never let one bad
    // row hide every other log.
    return timers.map((timer, index) => {
      const attributes = timer.attributes || {};
      const relationships = timer.relationships || {};
      const task = attributes.task || null;

      return new LogTimer({
        id: timer.id,
        index,
        createdAt: attributes.created_at,
        updatedAt: attributes.updated_at,
        date: attributes.date,
        timeSpend: attributes.time_spend,
        description: attributes.description,
        user: relationships.users ? relationships.users.data : null,
        isRunning: attributes.is_running,
        startTime: attributes.start_time,
        isClose: attributes.has_been_close,
        top: attributes.position_top,
        left: attributes.position_left,
        taskID: attributes.task_id,
        stepID: task ? task.step_id : null,
        project: project,
        exitDateTime: attributes.exit_date_time,
        task,
        tags: (relationships.tags && Array.isArray(relationships.tags.data)
          ? relationships.tags.data
          : []
        ).map((tag) => ({
          variant: tag.attributes.variant,
          label: tag.attributes.label,
          id: tag.id,
        })),
      });
    });
  }

  run() {
    return this.data;
  }
}

export class QueryNotifications {
  constructor(params) {
    this.data = this.make(params);
  }

  make(notifications) {
    return notifications?.map((notification) => {
      return new Notification({
        id: notification.id,
        created_at: notification.attributes.created_at,
        has_read: notification.attributes.has_read,
        updated_at: notification.attributes.updated_at,
        models: notification.attributes.models,
        object: notification.attributes.object,
        content: notification.attributes.content,
        isWritable: notification.attributes.owner,

        owner: {
          id: notification?.relationships?.users?.data?.id,
          email: notification?.relationships?.users?.data?.attributes?.email,
          firstName:
            notification?.relationships?.users?.data?.attributes?.first_name,
          phone: notification?.relationships?.users?.data?.attributes?.phone,
          lastName: notification?.relationships?.users?.data?.attributes?.last_name,
          role: notification?.relationships?.users?.data?.attributes?.role,
          title: `${notification?.relationships?.users?.data?.attributes?.first_name} ${notification?.relationships?.users?.data?.attributes?.last_name}`,
          alt: `${notification?.relationships?.users?.data?.attributes?.first_name} ${notification?.relationships?.users?.data?.attributes?.last_name}`,
          subTitle: notification?.relationships?.users?.data?.attributes?.email,
          background: notification?.relationships?.users?.data?.attributes?.color,
        },

        message: JSON.parse(notification.attributes.message),
      });
    });
  }

  run() {
    return this.data;
  }
}

export class QueryTrigger {
  constructor(params) {
    this.data = this.make(params);
  }

  make(params) {
    try {
      return params.map((trigger) => {
        return new Trigger({
          created_at: trigger.attributes.created_at,
          updated_at: trigger.attributes.updated_at,
          status: trigger.attributes.status,
          title: trigger.attributes.name,
          triggerID: trigger.attributes.trigger_id,
          triggerName: trigger.relationships.triggers.data.attributes.name,
          id: trigger.id,
          tags: trigger.attributes.tags,
          // both are read back by the edit sidebar; without them canBeSave stays false
          task_tag_ids: trigger.attributes.task_tag_ids,
          move_to_step_id: trigger.attributes.move_to_step_id,
          set_completed: trigger.attributes.set_completed,
          reply_snippet_id: trigger.attributes.reply_snippet_id,
          contacts: trigger.attributes.contacts,
          options: trigger.relationships.trigger_options.data,
          custom_field_id: trigger.attributes.custom_field_id,
          default: trigger.attributes.default,
        });
      });
    } catch (err) {
      console.log(err);
    }
  }

  run() {
    return this.data;
  }
}

export class QueryTaskForm {
  constructor(params) {
    this.data = this.make(params);
  }

  make(params) {
    return params.map((form) => new TaskForm(form));
  }

  run() {
    return this.data;
  }
}

export class QueryTemplate {
  constructor(params) {
    this.data = this.make(params);
  }

  make(params) {
    return params.map((template) => new Template(template));
  }

  run() {
    return this.data;
  }
}


export class QuerySnippet {
  constructor(params) {
    this.data = this.make(params);
  }

  make(params) {
    return params.map((template) => new Snippet(template));
  }

  run() {
    return this.data;
  }
}
